import { Icon } from '@iconify/react';
/**
 * The two screens a person meets before the app exists for them.
 *
 * His sign-in is a lockup on an open page — two soft washes of colour behind
 * it, a 64px field, and one black button the width of the phone — not a card
 * with a border sitting in the middle of a white screen.
 *
 * Shared by sign-in and by the household setup that follows it, so the first
 * two things anyone sees are the same thing twice.
 */
import type { ReactNode } from 'react';
import { t } from '../i18n';

export function AuthShell({
  title,
  subtitle,
  children,
  footer,
  steps,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** His progress dots: how many answers this screen wants, and how many are in. */
  steps?: { total: number; done: number };
}) {
  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center justify-center overflow-hidden px-8 py-12">
      {/* Ambient, and deliberately not animated: this Mac has Reduce Motion on
          and so do a lot of phones — a wash that only exists while it moves is
          a wash half the people never see. */}
      <div className="pointer-events-none absolute -end-[20%] -top-[20%] aspect-square w-[80%] rounded-full bg-task/15 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-[10%] -start-[10%] aspect-square w-[60%] rounded-full bg-magic/15 blur-[100px]" />

      <div className="relative z-10 w-full max-w-sm space-y-12">
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-24 w-24 items-center justify-center rounded-[2.5rem] bg-primary text-primary-foreground shadow-2xl shadow-black/10">
            <Icon icon="ph:house-fill" className="text-5xl" />
          </div>
          <div className="text-center">
            <h1 className="font-heading text-4xl font-bold tracking-tight">{title}</h1>
            <p className="mt-2 text-sm font-medium tracking-wide text-muted-foreground">
              {subtitle ?? t('The art of household management.')}
            </p>
          </div>
        </div>

        {children}

        {footer && <div className="text-center">{footer}</div>}
      </div>

      {steps && steps.total > 0 && (
        <div className="absolute bottom-10 start-0 end-0 flex justify-center px-8">
          <div className="flex gap-1.5 opacity-40">
            {Array.from({ length: steps.total }, (_, i) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: these are positional progress dots generated from a count — the index IS the identity and the list never reorders
                key={i}
                className={`h-1.5 w-1.5 rounded-full ${i < steps.done ? 'bg-primary' : 'bg-border'}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** His 64px field: a label above it, no border, and room for a thumb. */
export function AuthField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  /** id of the control this labels. The caller owns it because the control is
   *  passed in as children — PasswordInput, for one, wraps its input in a div,
   *  so wrapping the whole child in the <label> would also swallow its
   *  Show/Hide button. */
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label
        htmlFor={htmlFor}
        className="ms-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

export const AUTH_INPUT =
  'h-16 w-full rounded-2xl border-none bg-secondary px-6 text-sm font-bold placeholder:text-muted-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary';

export const AUTH_BUTTON =
  'h-16 w-full rounded-2xl bg-primary text-base font-bold text-primary-foreground shadow-xl shadow-primary/20 transition-transform active:scale-[0.98] disabled:opacity-40';
